import React, { createRef, forwardRef, useEffect } from 'react';
import { render } from 'react-dom';
import invariant from 'invariant';
import { DOMParser, DOMSerializer, Schema } from 'prosemirror-model';
import { Plugin, PluginKey, EditorState, TextSelection } from 'prosemirror-state';
import { EventEmitter } from 'events';
import { EditorView } from 'prosemirror-view';
import { keydownHandler } from 'prosemirror-keymap';
import { inputRules as inputRules$1 } from 'prosemirror-inputrules';
import 'prosemirror-commands';
import { createInstance } from '@sailor/i18n-web';

var ExtensionType;
(function(ExtensionType) {
    ExtensionType["Node"] = "node";
    ExtensionType["Mark"] = "mark";
    ExtensionType["NodeEmbed"] = "node-embed";
})(ExtensionType || (ExtensionType = {}));

/**
 * 统一样式前缀
 */ var CLASSNAME_PREFIX = "ct";

function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
var isOfType = function(type) {
    return function(value) {
        return (typeof value === "undefined" ? "undefined" : _typeof(value)) === type;
    };
};
var isString = isOfType("string");
var isPlainObject = function(obj) {
    if (typeof obj !== "object" || obj === null) return false;
    var proto = obj;
    while(Object.getPrototypeOf(proto) !== null){
        proto = Object.getPrototypeOf(proto);
    }
    return Object.getPrototypeOf(obj) === proto;
};
var isDOMNode = function(domNode) {
    return _instanceof(domNode, Node);
};
var omit = function(target, omitKey) {
    var filterKey = isString(omitKey) ? [
        omitKey
    ] : omitKey;
    if (isPlainObject(target)) {
        return Object.keys(target).filter(function(key) {
            return !filterKey.includes(key);
        }).reduce(function(prev, cur) {
            prev[cur] = target[cur];
            return prev;
        }, Object.create(null));
    }
    return target.filter(function(p) {
        return !filterKey.includes(p);
    });
};
var classNamePrefix = function(className) {
    var prefix = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : CLASSNAME_PREFIX;
    return "".concat(prefix, "-").concat(className);
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var findSelectionContext = function(selection) {
    var $from = selection.$from;
    var depth = [];
    for(var i = $from.depth; i > 0; i--){
        var node = $from.node(i);
        depth.push(node.type.name);
    }
    return depth;
};
// 参考 https://github.com/ProseMirror/prosemirror-inputrules/blob/master/src/inputrules.js
var MAX_MATCH = 500;
var _run = function(view, from, to, text, rules, plugin) {
    if (view.composing) return false;
    var state = view.state;
    var $from = state.doc.resolve(from);
    if ($from.parent.type.spec.code) return false;
    var textBefore = $from.parent.textBetween(Math.max(0, $from.parentOffset - MAX_MATCH), $from.parentOffset, null, "￼") + text;
    for(var i = 0; i < rules.length; i++){
        var match = rules[i].match.exec(textBefore);
        var tr = match && rules[i].handler(state, match, from - (match[0].length - text.length), to);
        if (!tr) {
            continue;
        }
        view.dispatch(tr.setMeta(plugin, {
            transform: tr,
            from: from,
            to: to,
            text: text
        }));
        return true;
    }
    return false;
};
var inputRules = function(param) {
    var rules = param.rules;
    var plugin = inputRules$1({
        rules: rules
    });
    var spec = _objectSpreadProps(_objectSpread({}, plugin.spec), {
        key: new PluginKey("inputRules")
    });
    spec.props.handleDOMEvents.keydown = function(view, event) {
        if (event.key !== "Enter") return false;
        var $cursor = view.state.selection.$cursor;
        if ($cursor) {
            return _run(view, $cursor.pos, $cursor.pos, "\n", rules, plugin) && // 防止插入块级元素后在元素内执行Enter操作
            (event.preventDefault() || true);
        }
        return false;
    };
    return new Plugin(spec);
};
function isEmptyDoc(doc) {
    return doc.childCount === 1 && doc.firstChild.isTextblock && doc.firstChild.content.size === 0;
}

/**
 * 通用配置
 */ var pluginKey = new PluginKey("image");

function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// 仅能放node mark的通用函数
var isNodeExtension = function(extension) {
    return extension.type === ExtensionType.Node;
};
var isMarkExtension = function(extension) {
    return extension.type === ExtensionType.Mark;
};
var isEmbedExtension = function(extension) {
    return extension.type === ExtensionType.NodeEmbed;
};
var hasExtensionProperty = function(key) {
    return function(extension) {
        return Boolean(extension[key]);
    };
};
var extensionKeyMapper = function(property, props) {
    return function(extension) {
        var extensionMethod = extension[property];
        var getFn = function() {
            if (isNodeExtension(extension)) {
                var _props_schema;
                return extensionMethod.bind(extension)(_objectSpreadProps$1(_objectSpread$1({}, props), {
                    type: (_props_schema = props.schema) === null || _props_schema === void 0 ? void 0 : _props_schema.nodes[extension.name]
                }));
            }
            if (isMarkExtension(extension)) {
                var _props_schema1;
                return extensionMethod.bind(extension)(_objectSpreadProps$1(_objectSpread$1({}, props), {
                    type: (_props_schema1 = props.schema) === null || _props_schema1 === void 0 ? void 0 : _props_schema1.marks[extension.name]
                }));
            }
            return extensionMethod.bind(extension)(props);
        };
        return getFn();
    };
};
var extensionMapperWithName = function(property, props) {
    return function(extension) {
        var extensionMethod = extension[property];
        var extensionName = extension.name;
        var getFn = function() {
            if (isNodeExtension(extension)) {
                var value = extensionMethod.bind(extension)(_objectSpreadProps$1(_objectSpread$1({}, props), {
                    type: props.schema.nodes[extension.name]
                }));
                return {
                    name: extensionName,
                    value: value
                };
            }
            if (isMarkExtension(extension)) {
                var value1 = extensionMethod.bind(extension)(_objectSpreadProps$1(_objectSpread$1({}, props), {
                    type: props.schema.marks[extension.name]
                }));
                return {
                    name: extensionName,
                    value: value1
                };
            }
            var value2 = extensionMethod.bind(extension)(props);
            return {
                name: extensionName,
                value: value2
            };
        };
        return getFn();
    };
};

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = '.ct-node-view-dom{max-width:100%;margin:0 2px;cursor:text}.ct-node-view-dom>*{cursor:auto}.ct-node-view-dom.ProseMirror-selectednode{outline:none}.ct-node-view-align-right>div{float:right!important;margin-top:0!important}.ct-node-view-align-center>div{margin:0 auto!important}.ct-node-view-clear-float:after{clear:both;content:".";display:block;width:0;height:0;visibility:hidden}';
styleInject(css_248z);

function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var PortalProvider = /*#__PURE__*/ function() {
    function PortalProvider() {
        _classCallCheck(this, PortalProvider);
        this.portals = new Map();
    }
    var _proto = PortalProvider.prototype;
    _proto.render = function render1(component, container) {
        this.portals.set(container, {
            component: component
        });
        render(component, container);
    };
    _proto.remove = function remove(container) {
        this.portals.delete(container);
        render(null, container);
    };
    return PortalProvider;
}();

function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
var nodeViewNamePrefix = function(cls) {
    return classNamePrefix("node-view-".concat(cls));
};
var reactNodeClass = classNamePrefix("node-view-dom");
var ReactNodeview = /*#__PURE__*/ function() {
    function ReactNodeview(node, view, getPos, decorations, portalProvider, reactComponent, parameters) {
        var _this = this;
        _classCallCheck$1(this, ReactNodeview);
        this.init = function() {
            _this.dom = _this.createDomRef();
            _this.dom.classList.add(reactNodeClass);
            _this.setDomAttrs(_this.node, _this.dom);
        };
        this.attrs = node.attrs;
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.decorations = decorations;
        this.portalProvider = portalProvider;
        this.parameters = parameters;
        this.reactComponent = reactComponent;
        this.ref = /*#__PURE__*/ createRef();
        this.nodeViewStyle = _objectSpread$2({
            display: node.isBlock ? "block" : "inline-block"
        }, reactComponent.nodeViewStyle);
        this.init();
        if (node.attrs && node.attrs.className) {
            this.dom.classList.add(node.attrs.className);
        }
        if (reactComponent.ignoreMutation) {
            this.ignoreMutation = reactComponent.ignoreMutation;
        }
        if (reactComponent.stopEvent) {
            var _reactComponent_stopEvent;
            this.stopEvent = (_reactComponent_stopEvent = reactComponent.stopEvent) !== null && _reactComponent_stopEvent !== void 0 ? _reactComponent_stopEvent : function() {
                return false;
            };
        }
        this.renderReactComponent();
    }
    var _proto = ReactNodeview.prototype;
    _proto.applyBubblingAttrs = function applyBubblingAttrs(node, element) {
        var _this = this;
        var attrs = node.attrs;
        Object.keys(this.nodeViewStyle).forEach(function(styleKey) {
            _this.dom.style[styleKey] = _this.nodeViewStyle[styleKey];
        });
        element.classList.remove(nodeViewNamePrefix("clear-float"), nodeViewNamePrefix("align-center"), nodeViewNamePrefix("align-right"));
        if (attrs.align === "center") {
            element.classList.add(nodeViewNamePrefix("clear-float"), nodeViewNamePrefix("align-center"));
        }
        if (attrs.align === "right") {
            element.classList.add(nodeViewNamePrefix("clear-float"), nodeViewNamePrefix("align-right"));
        }
    };
    _proto.setDomAttrs = function setDomAttrs(node, element) {
        this.applyBubblingAttrs(this.node, this.dom);
        var toDOM = this.node.type.spec.toDOM;
        if (toDOM) {
            var domSpec = toDOM(node);
            if (isString(domSpec) || isDOMNode(domSpec)) {
                return;
            }
            var attrs = domSpec[1];
            if (isPlainObject(attrs)) {
                omit(Object.keys(attrs), [
                    "style",
                    "class"
                ]).forEach(function(attr) {
                    element.setAttribute(attr, String(attrs[attr]));
                });
                return;
            }
        }
        omit(Object.keys(node.attrs), [
            "style",
            "class"
        ]).forEach(function(attr) {
            element.setAttribute(attr, node.attrs[attr]);
        });
    };
    _proto.createDomRef = function createDomRef() {
        var node = this.node;
        var toDOM = node.type.spec.toDOM;
        if (toDOM) {
            var domSpec = toDOM(node);
            if (typeof domSpec === "string") {
                return document.createElement(domSpec);
            }
            return document.createElement(domSpec[0]);
        }
        return node.isInline ? document.createElement("span") : document.createElement("div");
    };
    _proto.renderReactComponent = function renderReactComponent() {
        this.portalProvider.render(/*#__PURE__*/ React.createElement(this.reactComponent, _extends({}, this.parameters, {
            attrs: this.attrs,
            type: this.node.type.name,
            contentDOM: this.contentDOM,
            view: this.view,
            getPos: this.getPos,
            node: this.node,
            dom: this.dom
        })), this.dom);
    };
    _proto.update = function update(node, decorations) {
        if (this.reactComponent.pmNodeViewShouldUpdate) {
            return this.reactComponent.pmNodeViewShouldUpdate(node, decorations);
        }
        // 此函数有可能出现传入的 node 类型与原节点类型不一致的情况（https://prosemirror.net/docs/ref/#view.NodeView.update）
        // 需要再次进行判断，若类型不一致则说明更新的非源节点，使用默认处理。
        if (node.type !== this.node.type) {
            // this.destroy();
            return false;
        }
        if (this.ref && !this.node.sameMarkup(node)) {
            this.node = node;
            this.attrs = node.attrs;
            this.renderReactComponent();
            this.setDomAttrs(this.node, this.dom);
        }
        this.decorations = decorations;
        return true;
    };
    _proto.destroy = function destroy() {
        this.portalProvider.remove(this.dom);
    };
    ReactNodeview.fromComponent = function fromComponent(component, portalProvider, parameters) {
        return function(node, view, getPos, decorations) {
            return new ReactNodeview(node, view, getPos, decorations, portalProvider, component, parameters);
        };
    };
    return ReactNodeview;
}();

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles(arr) {
    if (Array.isArray(arr)) return arr;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _defineProperty$3(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableRest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _objectSpread$3(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$3(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _toArray(arr) {
    return _arrayWithHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableRest();
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var chainKeyBindingCommands = function() {
    for(var _len = arguments.length, commands = new Array(_len), _key = 0; _key < _len; _key++){
        commands[_key] = arguments[_key];
    }
    return function(params) {
        if (!commands.length) {
            return false;
        }
        var _commands = _toArray(commands), command = _commands[0], rest = _commands.slice(1);
        var calledNext = false;
        var createNext = function() {
            for(var _len = arguments.length, nextCommands = new Array(_len), _key = 0; _key < _len; _key++){
                nextCommands[_key] = arguments[_key];
            }
            return function() {
                if (!nextCommands.length) {
                    return false;
                }
                calledNext = true;
                var _nextCommands = _toArray(nextCommands), nextRest = _nextCommands.slice(1);
                return chainKeyBindingCommands.apply(void 0, _toConsumableArray(nextCommands))(_objectSpreadProps$2(_objectSpread$3({}, params), {
                    next: createNext.apply(void 0, _toConsumableArray(nextRest))
                }));
            };
        };
        var next = createNext.apply(void 0, _toConsumableArray(rest));
        var status = command(_objectSpreadProps$2(_objectSpread$3({}, params), {
            next: next
        }));
        if (calledNext || status) {
            return status;
        }
        return next();
    };
};
var createDoc = function(schema, value) {
    var $container = document.createElement("div");
    $container.innerHTML = value;
    return DOMParser.fromSchema(schema).parse($container);
};

function _arrayLikeToArray$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$1(arr) {
    if (Array.isArray(arr)) return arr;
}
function _arrayWithoutHoles$1(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray$1(arr);
}
function _classCallCheck$2(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _defineProperty$4(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof$1(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _iterableToArray$1(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterableToArrayLimit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$1() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableSpread$1() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _objectSpread$4(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$4(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$3(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$3(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$3(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _slicedToArray(arr, i) {
    return _arrayWithHoles$1(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1();
}
function _toConsumableArray$1(arr) {
    return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1();
}
function _unsupportedIterableToArray$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
var Manager = /*#__PURE__*/ function() {
    function Manager(managerOptions) {
        var _this = this;
        _classCallCheck$2(this, Manager);
        this._destroying = false;
        this._dead = false;
        this.collectParameters = function() {
            return {
                schema: _this.schema,
                providers: _this.providers,
                editable: _this.editable,
                copyable: _this.copyable,
                editorEventEmitter: _this.editorEventEmitter,
                getState: function() {
                    var _this_editorView;
                    return (_this_editorView = _this.editorView) === null || _this_editorView === void 0 ? void 0 : _this_editorView.state;
                },
                getView: function() {
                    return _this.editorView;
                },
                commands: _this.commands,
                config: {
                    placeholder: _this.placeholder
                }
            };
        };
        this.createPlugins = function() {
            var plugins = _this.extensions.filter(hasExtensionProperty("plugin")).map(extensionKeyMapper("plugin", _this.collectParameters())).reduce(function(prev, cur) {
                if (Array.isArray(cur)) {
                    prev = _toConsumableArray$1(prev).concat(_toConsumableArray$1(cur));
                } else {
                    prev = _toConsumableArray$1(prev).concat([
                        cur
                    ]);
                }
                return prev;
            }, []);
            return _toConsumableArray$1(plugins).concat(_toConsumableArray$1(_this.keymaps), [
                _this.inputRules
            ]);
        };
        this.createNodeViews = function() {
            return _this.extensions.filter(hasExtensionProperty("nodeViews")).reduce(function(prev, cur) {
                var parameters = {
                    provider: _this.providers.get(cur.name),
                    getEditable: function() {
                        return _this.editable;
                    },
                    getCopyable: function() {
                        return _this.copyable;
                    },
                    editorEventEmitter: _this.editorEventEmitter
                };
                prev[cur.name] = ReactNodeview.fromComponent(cur.nodeViews, _this.portalProvider, parameters);
                return prev;
            }, {});
        };
        this.createClipboardSerializer = function() {
            var base = DOMSerializer.fromSchema(_this.schema);
            var overrideNodes = _this.extensions.filter(function(p) {
                return isNodeExtension(p) || isEmbedExtension(p);
            }).filter(hasExtensionProperty("clipboardSerializer")).map(extensionKeyMapper("clipboardSerializer", _this.collectParameters())).reduce(function(prev, cur) {
                prev = _objectSpread$4({}, prev, cur);
                return prev;
            }, {});
            var overrideMarks = _this.extensions.filter(function(p) {
                return isMarkExtension(p);
            }).filter(hasExtensionProperty("clipboardSerializer")).map(extensionKeyMapper("clipboardSerializer", _this.collectParameters())).reduce(function(prev, cur) {
                prev = _objectSpread$4({}, prev, cur);
                return prev;
            }, {});
            return {
                nodes: _objectSpread$4({}, base.nodes, overrideNodes),
                marks: _objectSpread$4({}, base.marks, overrideMarks)
            };
        };
        this.createClipboardParser = function() {
            var base = DOMParser.fromSchema(_this.schema);
            var overrideRules = _this.extensions.filter(hasExtensionProperty("clipboardParser")).map(extensionKeyMapper("clipboardParser", _this.collectParameters())).reduce(function(acc, cur) {
                return _toConsumableArray$1(acc).concat(_toConsumableArray$1(cur));
            }, []);
            return {
                schema: base.schema,
                // 保证 clipboardParser 的规则优先级更高
                rules: _toConsumableArray$1(overrideRules).concat(_toConsumableArray$1(base.rules))
            };
        };
        this.createEditorView = function(editorDOM, editorState) {
            _this.originalState = editorState;
            var clipboardSerializer = _this.createClipboardSerializer();
            var clipboardParser = _this.createClipboardParser();
            _this.editorView = new EditorView(editorDOM, {
                nodeViews: _this.nodeViews,
                state: editorState,
                editable: function() {
                    return _this.editable;
                },
                dispatchTransaction: _this.dispatchTransaction,
                clipboardSerializer: new DOMSerializer(clipboardSerializer.nodes, clipboardSerializer.marks),
                clipboardParser: new DOMParser(clipboardParser.schema, clipboardParser.rules)
            });
            /**
     * 取消编辑器的拼写校验，提高渲染性能
     */ _this.editorView.dom.setAttribute("spellcheck", "false");
            _this._autoFocus && _this.editorView.focus();
        };
        this.createKeyMap = function() {
            var extensionKeymaps = _this.extensions.filter(hasExtensionProperty("keys")).map(extensionKeyMapper("keys", _this.collectParameters()));
            var previousCommandsMap = new Map();
            var mappedCommands = Object.create(null);
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                var _loop = function() {
                    var _loop = function(key) {
                        if (!Object.prototype.hasOwnProperty.call(extensionKeymap, key)) {
                            return "continue";
                        }
                        var _previousCommandsMap_get;
                        var previousCommands = (_previousCommandsMap_get = previousCommandsMap.get(key)) !== null && _previousCommandsMap_get !== void 0 ? _previousCommandsMap_get : [];
                        var commands = _toConsumableArray$1(previousCommands).concat([
                            extensionKeymap[key]
                        ]);
                        var command = chainKeyBindingCommands.apply(void 0, _toConsumableArray$1(commands));
                        previousCommandsMap.set(key, commands);
                        mappedCommands[key] = function(state, dispatch, view) {
                            return command({
                                state: state,
                                dispatch: dispatch,
                                view: view,
                                next: function() {
                                    return false;
                                }
                            });
                        };
                    };
                    var extensionKeymap = _step.value;
                    for(var key in extensionKeymap)_loop(key);
                };
                for(var _iterator = extensionKeymaps[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true)_loop();
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
            return [
                new Plugin({
                    key: new PluginKey("keymap"),
                    props: {
                        handleKeyDown: keydownHandler(mappedCommands)
                    }
                })
            ];
        };
        this.createInputRules = function() {
            var rules = _this.extensions.filter(hasExtensionProperty("inputRules")).map(extensionKeyMapper("inputRules", _this.collectParameters())).reduce(function(prev, cur) {
                prev = _toConsumableArray$1(prev).concat(_toConsumableArray$1(cur));
                return prev;
            }, []);
            return inputRules({
                rules: rules
            });
        };
        this.createCommands = function() {
            var enhancedMethod = function(command) {
                return function() {
                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                        args[_key] = arguments[_key];
                    }
                    var _this_editorView = _this.editorView, state = _this_editorView.state, dispatch = _this_editorView.dispatch;
                    return command.apply(void 0, _toConsumableArray$1(args))(state, dispatch, _this.editorView);
                };
            };
            var flattenAndEnhanced = function(fnObj) {
                var result = Object.create(null);
                Object.entries(fnObj).forEach(function(param) {
                    var _param = _slicedToArray(param, 2), key = _param[0], value = _param[1];
                    result[key] = enhancedMethod(value);
                });
                return result;
            };
            return _this.extensions.filter(hasExtensionProperty("commands")).map(extensionMapperWithName("commands", _this.collectParameters())).reduce(function(prev, cur) {
                prev = _objectSpreadProps$3(_objectSpread$4({}, prev), _defineProperty$4({}, cur.name, flattenAndEnhanced(cur.value)));
                return prev;
            }, {});
        };
        this.methodFactory = function(method) {
            return function(args) {
                _this.editorView.focus();
                return method({
                    state: _this.editorView.state,
                    dispatch: _this.editorView.dispatch,
                    view: _this.editorView,
                    args: args
                });
            };
        };
        this.docHasChanged = function() {
            return !_this.originalState.doc.eq(_this.editorView.state.doc);
        };
        /**
   * 暂时解决 ios 下在标题后按回车换行两次的问题 ONES#10775656
   * 参考: https://discuss.prosemirror.net/t/unexpected-behavior-when-pressing-enter-safari-mobile/3226/3
   * 调研文档: https://km.sankuai.com/page/754050493
   * TODO: 升级 prosemirror-view 至 1.16.4 后可去除该逻辑
   */ this.skipIosEnter = function(view, tr) {
            return tr.selectionSet && !tr.docChanged && // 因为当前的prosemirrorView版本中，view.lastIOSEnterFallbackTimeout的时间设置是50ms
            view.lastIOSEnter > Date.now() - 50;
        };
        this.dispatchTransaction = function(tr) {
            if (_this.skipIosEnter(_this.editorView, tr)) return;
            var state = _this.editorView.state.apply(tr);
            _this.editorView.updateState(state);
            if (_this.stateUpdatedCallback) {
                _this.stateUpdatedCallback(_this, tr.docChanged);
            }
        };
        this.getSelectionContext = function() {
            return findSelectionContext(_this.editorView.state.selection);
        };
        this.destroyManager = function(cb) {
            var _this_editorView;
            if (_this._destroying || _this._dead) {
                return;
            }
            _this._destroying = true;
            (_this_editorView = _this.editorView) === null || _this_editorView === void 0 ? void 0 : _this_editorView.destroy();
            _this.providers.forEach(function(_) {
                var __destroy;
                return (__destroy = _.destroy) === null || __destroy === void 0 ? void 0 : __destroy.call(_, _this.collectParameters());
            });
            _this.extensions.forEach(function(_) {
                var // @ts-ignore 允许Manager调用destroy
                __destroy;
                (__destroy = _.destroy) === null || __destroy === void 0 ? void 0 : __destroy.call(_, _this.collectParameters());
            });
            cb(_this);
            _this._destroying = false;
            _this._dead = true;
        };
        invariant(!!managerOptions, "Manager实例化参数异常");
        var defaultValue = managerOptions.defaultValue, placeholder = managerOptions.placeholder, providers = managerOptions.providers, appendExtensions = managerOptions.appendExtensions, schema = managerOptions.schema, _managerOptions_editable = managerOptions.editable, editable = _managerOptions_editable === void 0 ? true : _managerOptions_editable, _managerOptions_autoFocus = managerOptions.autoFocus, autoFocus = _managerOptions_autoFocus === void 0 ? true : _managerOptions_autoFocus, _managerOptions_copyable = managerOptions.copyable, copyable = _managerOptions_copyable === void 0 ? true : _managerOptions_copyable;
        var _providers_providers;
        this.providers = (_providers_providers = providers === null || providers === void 0 ? void 0 : providers.providers) !== null && _providers_providers !== void 0 ? _providers_providers : new Map();
        this.defaultValue = defaultValue;
        this.placeholder = placeholder;
        this.extensions = appendExtensions || [];
        this.portalProvider = new PortalProvider();
        this.schema = schema;
        this.editable = editable;
        this.copyable = copyable;
        this.editorEventEmitter = new EventEmitter();
        this._autoFocus = autoFocus;
        invariant(_instanceof$1(this.schema, Schema), "schema异常");
    }
    var _proto = Manager.prototype;
    _proto.init = function init(editorRef, stateUpdatedCallback) {
        var _this = this;
        this.extensions.forEach(function(_) {
            var __willMountManager;
            return (__willMountManager = _.willMountManager) === null || __willMountManager === void 0 ? void 0 : __willMountManager.call(_, _this.collectParameters());
        });
        this.providers.forEach(function(_) {
            var __willMountManager;
            return (__willMountManager = _.willMountManager) === null || __willMountManager === void 0 ? void 0 : __willMountManager.call(_, _this.collectParameters());
        });
        this.stateUpdatedCallback = stateUpdatedCallback;
        this.commands = this.createCommands();
        this.keymaps = this.createKeyMap();
        // InputRule目前不支持Enter输入的识别，如果需要可参考：https://discuss.prosemirror.net/t/trigger-inputrule-on-enter/1118/4
        this.inputRules = this.createInputRules();
        this.nodeViews = this.createNodeViews();
        this.plugins = this.createPlugins();
        if (this.defaultValue) {
            if (typeof this.defaultValue === "string") {
                var editorState = EditorState.create({
                    doc: createDoc(this.schema, this.defaultValue),
                    schema: this.schema,
                    plugins: this.plugins
                });
                this.createEditorView(editorRef, editorState);
            } else {
                var editorState1 = EditorState.fromJSON({
                    schema: this.schema,
                    plugins: this.plugins
                }, this.defaultValue);
                this.createEditorView(editorRef, editorState1);
            }
        } else {
            var editorState2 = EditorState.create({
                schema: this.schema,
                plugins: this.plugins
            });
            this.createEditorView(editorRef, editorState2);
        }
        this.providers.forEach(function(_) {
            var __didMountManager;
            return (__didMountManager = _.didMountManager) === null || __didMountManager === void 0 ? void 0 : __didMountManager.call(_, _this.collectParameters());
        });
        this.extensions.forEach(function(_) {
            var __didMountManager;
            return (__didMountManager = _.didMountManager) === null || __didMountManager === void 0 ? void 0 : __didMountManager.call(_, _this.collectParameters());
        });
    };
    _createClass(Manager, [
        {
            key: "destroying",
            get: function get() {
                return this._destroying;
            }
        }
    ]);
    return Manager;
}();

var ReactEditor = /*#__PURE__*/ forwardRef(function(props, ref) {
    var width = props.width, _props_destroy = props.destroy, destroy = _props_destroy === void 0 ? function() {} : _props_destroy;
    useEffect(function() {
        return destroy;
    }, []);
    return /*#__PURE__*/ React.createElement("div", {
        className: "ct-editor",
        ref: ref,
        style: width ? {
            width: "".concat(width, "px")
        } : {}
    });
});

var name = "@it/parker";
var version = "0.96.0";
var description = "新编辑器";
var repository = "ssh://git@git.sankuai.com/in/ct-editor-plus.git";
var keywords = [];
var main = "es/core/index.js";
var module = "es/core/index.js";
var types = "typings/index.d.ts";
var files = [
    "es",
    "dist",
    "typings"
];
var license = "UNLICENSED";
var scripts = {
    "pub:feature": "node ./scripts/bp/bp.js --build_env=feature --nocheck",
    "pub:grey": "node ./scripts/bp/bp.js --build_env=grey --tag=grey",
    release: "node ./scripts/bp/release.js --fallback=self",
    lint: "eslint --ext js,ts,tsx src --max-warnings 0",
    "test:watch": "jest --watch",
    "test:coverage": "jest --coverage",
    "test:schema": "jest --coverage  --collectCoverageFrom=**/citadel-schema/**/*.ts",
    "test:doc-command": "jest --coverage  --collectCoverageFrom=**/doc-commands/**/*.ts",
    test: "node ./scripts/unit-test/index.js",
    "test:all": "jest",
    "dev:mobile": "parcel --no-cache examples/basic/mobile/index.html -p 1235 --dist-dir ./build",
    "dev:pc": "parcel --no-cache examples/basic/pc/index.html -p 1235 --dist-dir ./build",
    "dev:debug": "parcel --no-cache examples/basic/debug/index.html -p 1235 --dist-dir ./build",
    "dev:link": "rollup -w -c rollup.config.js",
    "dev:lite": "parcel --no-cache examples/basic/lite/index.html -p 1235 --dist-dir ./build",
    "build:es": "node --max_old_space_size=4096 -r @babel/register scripts/rollup/index.js",
    "build:dist": "node --max_old_space_size=4096 -r @babel/register scripts/rollup/buildIndex.js",
    build: "npm run build:es",
    storybook: "start-storybook -p 6006",
    changelog: "node ./scripts/changelog/index.js",
    i18nsync: "i18n sync -c i18n.config.json"
};
var peerDependencies = {
    react: ">=16.8.0",
    "react-dom": ">=16.8.0"
};
var dependencies = {
    "@babel/runtime": "7.16.3",
    "@mtfe/data2chart-react": "2.0.15",
    "@sailor/i18n-resource-sync": "0.2.1",
    "@sailor/i18n-web": "0.2.1",
    "broadcast-channel": "3.1.0",
    classnames: "2.2.6",
    codemirror: "5.64.0",
    "date-fns": "2.19.0",
    dompurify: "2.3.3",
    "eslint-plugin-prettier": "3.3.1",
    events: "3.2.0",
    "highlight.js": "10.7.1",
    invariant: "2.2.4",
    "lodash.chunk": "4.2.0",
    "lodash.debounce": "4.0.8",
    "lodash.intersection": "4.4.0",
    "lodash.isequal": "4.5.0",
    "lodash.throttle": "4.1.1",
    "lodash.union": "4.6.0",
    "markdown-it": "8.4.2",
    moment: "2.29.1",
    "plantuml-encoder": "1.4.0",
    "prosemirror-commands": "1.0.8",
    "prosemirror-dev-tools": "^2.1.1",
    "prosemirror-dropcursor": "1.3.2",
    "prosemirror-history": "1.0.4",
    "prosemirror-inputrules": "^1.1.2",
    "prosemirror-keymap": "^1.1.3",
    "prosemirror-model": "1.7.1",
    "prosemirror-schema-list": "1.1.2",
    "prosemirror-state": "1.2.2",
    "prosemirror-transform": "1.1.3",
    "prosemirror-view": "1.14.8",
    "rc-checkbox": "2.3.2",
    "rc-picker": "2.5.15",
    "rc-textarea": "0.3.3",
    "react-motion": "0.5.2",
    semver: "7.3.4",
    "style-loader": "1.2.1",
    "use-debounce": "7.0.0",
    uuid: "8.3.2"
};
var devDependencies = {
    "@babel/core": "^7.4.4",
    "@babel/plugin-proposal-class-properties": "7.8.3",
    "@babel/plugin-transform-modules-commonjs": "7.23.3",
    "@babel/plugin-transform-runtime": "7.9.6",
    "@babel/preset-env": "7.9.6",
    "@babel/preset-react": "7.9.4",
    "@babel/preset-typescript": "7.9.0",
    "@babel/register": "7.9.0",
    "@commitlint/cli": "^9.1.1",
    "@commitlint/config-angular": "^9.1.1",
    "@it/bp": "0.2.10",
    "@it/eslint-config-citadel": "1.0.20",
    "@parcel/transformer-less": "2.3.2",
    "@parcel/transformer-svg": "2.3.2",
    "@rollup/plugin-alias": "3.1.2",
    "@rollup/plugin-babel": "5.0.0",
    "@rollup/plugin-commonjs": "11.1.0",
    "@rollup/plugin-image": "2.0.4",
    "@rollup/plugin-json": "4.1.0",
    "@rollup/plugin-node-resolve": "7.1.3",
    "@rollup/plugin-virtual": "3.0.1",
    "@storybook/addon-actions": "^6.0.19",
    "@storybook/addon-essentials": "^6.0.19",
    "@storybook/addon-links": "^6.0.19",
    "@storybook/react": "^6.0.19",
    "@swc/core": "1.3.20",
    "@types/classnames": "2.2.10",
    "@types/events": "3.0.0",
    "@types/jest": "^24.0.13",
    "@types/map-obj": "3.1.0",
    "@types/node": "^12.0.2",
    "@types/prosemirror-commands": "^1.0.1",
    "@types/prosemirror-history": "^1.0.1",
    "@types/prosemirror-model": "^1.13.2",
    "@types/prosemirror-schema-basic": "^1.0.1",
    "@types/prosemirror-schema-list": "^1.0.1",
    "@types/prosemirror-state": "^1.2.3",
    "@types/react": "^16.9.35",
    "@types/react-dom": "16.9.8",
    "@types/uuid": "8.0.0",
    autoprefixer: "9.7.6",
    "babel-jest": "^24.8.0",
    "babel-loader": "^8.1.0",
    "babel-plugin-module-resolver": "^4.0.0",
    "branch-name": "1.0.0",
    chalk: "4.1.0",
    "crypto-js": "4.1.1",
    "css-loader": "4.2.2",
    cssnano: "4.1.10",
    esbuild: "0.13.15",
    "eslint-plugin-jest": "^22.5.1",
    "eslint-plugin-you-dont-need-momentjs": "^1.6.0",
    fs: "0.0.1-security",
    husky: "^3.0.3",
    jest: "26.6.3",
    "jest-environment-jsdom-sixteen": "1.0.3",
    "jest-svg-transformer": "1.0.0",
    "less-loader": "7.0.0",
    "lint-staged": "^9.2.1",
    nodemon: "^1.19.2",
    ora: "2.1.0",
    parcel: "2.3.2",
    path: "0.12.7",
    postcss: "^8.0.0",
    "postcss-url": "8.0.0",
    "promise-limit": "2.7.0",
    react: ">=16.9.0",
    "react-dom": ">=16.9.0",
    "react-is": "^16.13.1",
    rollup: "2.8.0",
    "rollup-plugin-dts": "2.0.1",
    "rollup-plugin-ignore-import": "1.3.2",
    "rollup-plugin-node-builtins": "2.1.2",
    "rollup-plugin-postcss": "3.1.1",
    "rollup-plugin-progress": "1.1.2",
    "rollup-plugin-terser": "^7.0.2",
    "rollup-plugin-typescript2": "0.30.0",
    signale: "1.4.0",
    "standard-version": "^9.3.0",
    "style-loader": "1.2.1",
    stylus: "0.54.8",
    "ts-jest": "26.5.1",
    "ts-node": "^8.1.1",
    tslib: "2.1.0",
    typescript: "4",
    "typescript-plugin-css-modules": "^1.2.1",
    "yargs-parser": "21.0.0"
};
var resolutions = {
    "prosemirror-commands": "1.0.8",
    "prosemirror-dropcursor": "1.3.2",
    "prosemirror-history": "1.0.4",
    "prosemirror-state": "1.2.2",
    "prosemirror-transform": "1.1.3",
    "prosemirror-model": "1.7.1",
    "@types/prosemirror-commands": "^1.0.1",
    "@types/prosemirror-history": "^1.0.1",
    "@types/prosemirror-model": "^1.13.2",
    "@types/prosemirror-schema-basic": "^1.0.1",
    "@types/prosemirror-schema-list": "^1.0.1",
    "@types/prosemirror-state": "^1.2.3"
};
var husky = {
    hooks: {
        "pre-commit": "lint-staged",
        "commit-msg": "commitlint -E HUSKY_GIT_PARAMS"
    }
};
var alias = {
    basic: "./examples/basic",
    "@/*": "./src/$1"
};
var targets = {
    main: false
};
var PackageJSON = {
    name: name,
    version: version,
    description: description,
    repository: repository,
    keywords: keywords,
    main: main,
    module: module,
    types: types,
    files: files,
    license: license,
    scripts: scripts,
    peerDependencies: peerDependencies,
    dependencies: dependencies,
    devDependencies: devDependencies,
    resolutions: resolutions,
    husky: husky,
    "lint-staged": {
        "*.{js,jsx,ts,tsx}": [
            "eslint"
        ]
    },
    alias: alias,
    targets: targets
};

var css_248z$1 = '.ProseMirror *,.ProseMirror :after,.ProseMirror :before{box-sizing:border-box}.ct-editor h1,.ct-editor h2,.ct-editor h3,.ct-editor h4,.ct-editor h5,.ct-editor h6,.ct-editor p,.ct-editor pre{margin:8px 0;color:#333;word-break:break-word;clear:both}.ct-editor p,.ct-editor pre{font-size:14px;line-height:22px;min-height:24px}.ct-editor h1{font-size:28px;font-weight:700;line-height:42px;min-height:42px}.ct-editor h2{font-size:24px;font-weight:700;line-height:36px;min-height:36px}.ct-editor h3{font-size:20px;font-weight:700;line-height:30px}.ct-editor h4{font-size:16px;font-weight:700;line-height:22px;min-height:24px}.ct-editor h5{font-size:14px;font-weight:700;line-height:21px;min-height:21px}.ct-editor h6{font-size:12px;font-weight:700;line-height:18px;min-height:18px}.ct-editor hr{display:block;height:1px;width:100%;border:0;background:#ccc}.ProseMirror{position:relative;word-wrap:break-word;white-space:pre-wrap;white-space:break-spaces;font-variant-ligatures:none;font-feature-settings:"liga" 0}.ProseMirror p{font-size:14px;line-height:22px;min-height:24px;margin:8px 0;color:#333;word-break:break-word;clear:both}.ProseMirror pre{white-space:pre-wrap}.ProseMirror li{position:relative}.ProseMirror-hideselection *::-moz-selection{background:transparent}.ProseMirror-hideselection ::selection{background:transparent}.ProseMirror-hideselection ::-moz-selection{background:transparent}li.ProseMirror-selectednode{outline:none}li.ProseMirror-selectednode:after{content:"";position:absolute;left:-32px;right:-2px;top:-2px;bottom:-2px;border:2px solid #8cf;pointer-events:none}.ct-editor-container{display:flex;justify-content:center;height:100%;overflow:auto}.ct-editor-container .ct-editor{position:relative;width:100%;height:100%;box-sizing:content-box}.ct-editor-container .ct-editor .ProseMirror{outline:none;margin-left:0;padding:0 24px}';
styleInject(css_248z$1);

function _classCallCheck$3(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties$1(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass$1(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties$1(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties$1(Constructor, staticProps);
    return Constructor;
}
var Editor = /*#__PURE__*/ function() {
    function Editor(dom, config) {
        var _this = this;
        _classCallCheck$3(this, Editor);
        // changeCallback 触发时是否返回文档内容
        this.withValue = true;
        this._destroying = false;
        this._dead = false;
        this.initConfig = function() {
            var _this_config = _this.config, _this_config_appendExtensions = _this_config.appendExtensions, appendExtensions = _this_config_appendExtensions === void 0 ? [] : _this_config_appendExtensions, providers = _this_config.providers, schema = _this_config.schema, _this_config_defaultValue = _this_config.defaultValue, defaultValue = _this_config_defaultValue === void 0 ? null : _this_config_defaultValue, _this_config_placeholder = _this_config.placeholder, placeholder = _this_config_placeholder === void 0 ? "" : _this_config_placeholder, _this_config_editable = _this_config.editable, editable = _this_config_editable === void 0 ? true : _this_config_editable, _this_config_autoFocus = _this_config.autoFocus, autoFocus = _this_config_autoFocus === void 0 ? true : _this_config_autoFocus, _this_config_copyable = _this_config.copyable, copyable = _this_config_copyable === void 0 ? true : _this_config_copyable;
            _this.manager = new Manager({
                appendExtensions: appendExtensions,
                providers: providers,
                defaultValue: defaultValue,
                placeholder: placeholder,
                schema: schema,
                editable: editable,
                autoFocus: autoFocus,
                copyable: copyable
            });
        };
        this.addMenubar = function(menubar) {
            _this.menubar = menubar;
            _this.updateMenubar = function() {
                menubar.update();
            };
        };
        this.bindUpdate = function(method) {
            invariant(typeof method === "function", "bindUpdate的参数必须是一个函数");
            _this.updateMenubar = method;
        };
        this.init = function() {
            var callback = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : function() {};
            render(/*#__PURE__*/ React.createElement(ReactEditor, {
                ref: _this.onRef,
                manager: _this.manager,
                destroy: _this.destroy.bind(_this)
            }), _this.dom, callback);
            if (_this.menubar) _this.menubar.connect(_this);
        };
        this.onRef = function(ref) {
            if (ref) {
                _this.editorRef = ref;
                _this.bindManager();
            }
        };
        this.stateUpdateCallback = function(manager, docChanged) {
            if (_this.updateMenubar) _this.updateMenubar(manager);
            if (docChanged && _this.changeCallback) {
                var value = _this.withValue ? _this.value() : undefined;
                _this.changeCallback(value);
            }
        };
        this.bindManager = function() {
            _this.manager.init(_this.editorRef, _this.stateUpdateCallback);
        };
        /**
   * 当前文档状态变化时触发回调
   * @param method 回调函数
   * @param withValue 是否将当前文档内容传给回调函数，默认为 true
   */ this.onChange = function(method) {
            var withValue = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
            _this.changeCallback = method;
            _this.withValue = withValue;
        };
        this.value = function(value) {
            var editorView = _this.manager.editorView;
            if (!editorView) return undefined;
            var _editorView_state = editorView.state, doc = _editorView_state.doc, schema = _editorView_state.schema, plugins = _editorView_state.plugins;
            // 取值
            if (typeof value !== "string") {
                var $container = document.createElement("div");
                var fragment = DOMSerializer.fromSchema(schema).serializeFragment(doc.content);
                $container.appendChild(fragment);
                return $container.innerHTML;
            }
            // 赋值
            var newDoc = createDoc(schema, value);
            var newState = EditorState.create({
                doc: newDoc,
                schema: schema,
                plugins: plugins
            });
            editorView.updateState(newState);
            return undefined;
        };
        this.focus = function() {
            _this.manager.editorView.focus();
        };
        this.setCursor = function() {
            var pos = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : "start";
            var _this_manager;
            if (!((_this_manager = _this.manager) === null || _this_manager === void 0 ? void 0 : _this_manager.editorView)) return;
            var _this_manager_editorView = _this.manager.editorView, dispatch = _this_manager_editorView.dispatch, _this_manager_editorView_state = _this_manager_editorView.state, tr = _this_manager_editorView_state.tr, doc = _this_manager_editorView_state.doc;
            if (pos === "start" || isEmptyDoc(doc)) {
                tr.setSelection(TextSelection.create(doc, 1));
                dispatch(tr.scrollIntoView());
            } else {
                tr.setSelection(TextSelection.near(doc.resolve(doc.content.size)));
                dispatch(tr.scrollIntoView());
            }
            _this.manager.editorView.focus();
        };
        this.config = config;
        this.enhanceDom(dom);
        this.initConfig();
    }
    var _proto = Editor.prototype;
    _proto.enhanceDom = function enhanceDom(dom) {
        var editorWrapper = document.createElement("div");
        dom.appendChild(editorWrapper);
        this.dom = editorWrapper;
        this.dom.classList.add(Editor.ROOT_CLASS_NAME);
    };
    _proto.destroy = function destroy() {
        var _this, _this__destroyCallback;
        (_this__destroyCallback = (_this = this)._destroyCallback) === null || _this__destroyCallback === void 0 ? void 0 : _this__destroyCallback.call(_this, this);
        this._destroying = false;
        this._dead = true;
    };
    _proto.destroyEditor = function destroyEditor(cb) {
        var _this = this;
        var _this_manager;
        if (this._destroying || this._dead) {
            return false;
        }
        this._destroying = true;
        this._destroyCallback = cb;
        (_this_manager = this.manager) === null || _this_manager === void 0 ? void 0 : _this_manager.destroyManager(function() {
            render(null, _this.dom);
        });
        return true;
    };
    _createClass$1(Editor, [
        {
            key: "version",
            get: function get() {
                return PackageJSON.version;
            }
        }
    ]);
    return Editor;
}();
Editor.ROOT_CLASS_NAME = "ct-editor-container";

function _classCallCheck$4(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var Providers = function Providers() {
    var _this = this;
    _classCallCheck$4(this, Providers);
    this.registerProvider = function(name, provider) {
        if (!provider) {
            throw new Error("provider of ".concat(name, " not provided"));
        }
        _this.providers.set(name, provider);
    };
    this.providers = new Map();
};

var locale = "en";
var version$1 = 16;
var cannot_search_user = "User not found, click space to complete input";
var load_fail_retry = "Failed to load, please try again later";
var pk_avatar = "avatar";
var mention_by_mis_name = "Can @user by name and MIS number";
var searching = "Searching...";
var pk_link_text = "Text";
var insert_blank = "Insert";
var pk_visit_link = "Access the link";
var pk_link_address = "Address";
var pk_link = "Link";
var cancel_blank = "Cancel";
var pk_link_change_tip = "Link text follows the Doc change";
var pk_edit_link = "Edit link";
var pk_link_text_tip = "Input the link text";
var pk_cancel_link = "Cancel link";
var save_blank = "Save";
var pk_link_address_tip = "Input the link address";
var border = "Border";
var pk_insert = "Insert";
var image_link = "Image Link";
var pk_retry = "Retry";
var pk_edit = "Edit";
var width = "Width";
var reupload = "Reupload";
var image_upload_fail1 = "Sorry, upload error!";
var right_address_tip = "Please input right address";
var image_upload_fail3 = "Otherwise, please click button below to retry.";
var image_upload_fail2 = "If the image is moved/pasted from outside, please download and reupload.";
var pk_confirm = "Confirm";
var input_html = "Enter HTML code.";
var formual = "Formual";
var pk_loading = "Loading...";
var click_to_expand = "Click to expand the content";
var pk_cancel = "Cancel";
var input_html_tip = "You can insert HTML code to embed corresponding content on a webpage.";
var input_latex = "Enter Latex code";
var insert_latex_tip = "You can insert Latex code to embed corresponding content within the page.";
var pk_title = "Title";
var wire_frame = "Wireframe";
var fill_in = "Fill";
var input_plantuml = "Enter PlantUML code.";
var border_style = "Border type";
var set_th_line = "Header Column";
var set_th_row = "Header Row";
var table_attr = "Table style";
var cancel_th_line = "Cancel Header Column";
var insert_line_right = "Right Insert Column";
var insert_row = "Delete Rows";
var split_cell = "Split cells";
var delete_line = "Delete Columns";
var pk_no = "No borders";
var input_markdown_tip = "You can insert Markdown code to embed corresponding content within the page.";
var confirm_blank = "Confirm";
var cancel_th_row = "Cancel Header Row";
var insert_row_above = "Insert Row Above";
var delete_table = "Delete";
var input_plantuml_tip = "You can insert PlantUML code to embed corresponding content within the page.";
var border_width = "Border width";
var input_markdown = "Enter Markdown code.";
var merge_cell = "Merge cells";
var set_line_num = "Show Row Number";
var border_color = "Border color";
var insert_line_left = "Left Insert Column";
var insert_row_below = "Insert Row Below";
var cancel_line_num = "Cancel Row Number";
var exit_data_lost_tip = "If you exit, any unsaved data will be lost.";
var if_exit_gantt = "Are you sure to exit the Gantt chart?";
var pk_gantt_loading = "Loading Gantt chart...";
var gantt_load_fail = "Gantt chart loading failed.";
var operate_error = "Invalid operation";
var gantt_keep_work_tip = "Please keep at least one task.";
var save_success = "Save successful";
var gantt_frame_load_fail = "Gantt chart framework failed to load.";
var pk_network_exception = "Network exception";
var save_fail = "Save failed";
var pk_saving = "Saving...";
var gantt_plugin_fail = "Gantt chart editing plugin failed to start.";
var gantt_editing_tip = "Editing Gantt Chart, please wait a moment.";
var pk_discard = "Drop";
var pk_zoom_in = "Zoom in";
var pk_zoom_out = "Zoom out";
var pk_use = "Use";
var draft_edit_tip = "There is a draft that was edited but not saved on {time}. Do you want to continue editing?";
var if_use_local_draft = "Do you use local drafts?";
var pk_load_fail = "Loading failed";
var pk_this_time = "Now";
var choose_time = "Select time";
var pk_hh_mm_ss = "hh:mm:ss A";
var pk_y_d = "MM-DD-YYYY";
var pk_y_d_ts = "MM-DD-YYYY hh:mm:ss A";
var pk_text = "Text";
var h1 = "Heading 1";
var h2 = "Heading 2";
var h3 = "Heading 3";
var h4 = "Heading 4";
var h5 = "Heading 5";
var h6 = "Heading 6";
var pk_catalog = "Catalog";
var page_view_load_fail_click = "Directory tree loading failed. Please click to reload.";
var page_tree_no_sub_doc = "No sub documents found in this directory";
var page_tree_no_perm = "No permission to view the directory tree.";
var pk_load_fail_retry = "Loading failed, please click to reload.";
var loading2 = "Loading";
var page_tree_deleted = "The directory tree has been deleted.";
var pk_pause = "Pause";
var pk_mute = "Mute";
var pk_untitled = "Untitled";
var pk_play = "Play";
var adjust_volume = "Adjust volume";
var download_fail_retry = "Download failed please retry";
var pk_download = "Download";
var get_file_fail = "Failed to get file";
var pk_network_fail = "Network connection failed";
var pk_upload_fail = "Upload failed";
var audio_not_support_tip = "This browser does not support the HTML5 audio tag.";
var pk_volume = "Volume";
var browser_not_support_tip = "This browser does not support the HTML5 video tag.";
var pk_zoom_in2 = "Zoom in";
var video_upload_tip = "Video is being transcoded, if the file is large, it may take a long time. Please wait patiently...";
var pk_video = "Video";
var pk_zoom_out2 = "Zoom out";
var space_update_fail_tip = "Space update acquisition failed. Click to retry.";
var pk_recent_update = "Recent Updates";
var show_more = "Show More";
var no_space_update = "No Updates in Space.";
var pk_restore = "Restore";
var en_parker = {
    locale: locale,
    version: version$1,
    cannot_search_user: cannot_search_user,
    load_fail_retry: load_fail_retry,
    pk_avatar: pk_avatar,
    mention_by_mis_name: mention_by_mis_name,
    searching: searching,
    pk_link_text: pk_link_text,
    insert_blank: insert_blank,
    pk_visit_link: pk_visit_link,
    pk_link_address: pk_link_address,
    pk_link: pk_link,
    cancel_blank: cancel_blank,
    pk_link_change_tip: pk_link_change_tip,
    pk_edit_link: pk_edit_link,
    pk_link_text_tip: pk_link_text_tip,
    pk_cancel_link: pk_cancel_link,
    save_blank: save_blank,
    pk_link_address_tip: pk_link_address_tip,
    border: border,
    pk_insert: pk_insert,
    image_link: image_link,
    pk_retry: pk_retry,
    pk_edit: pk_edit,
    width: width,
    reupload: reupload,
    image_upload_fail1: image_upload_fail1,
    right_address_tip: right_address_tip,
    image_upload_fail3: image_upload_fail3,
    image_upload_fail2: image_upload_fail2,
    pk_confirm: pk_confirm,
    input_html: input_html,
    formual: formual,
    pk_loading: pk_loading,
    click_to_expand: click_to_expand,
    pk_cancel: pk_cancel,
    input_html_tip: input_html_tip,
    input_latex: input_latex,
    insert_latex_tip: insert_latex_tip,
    pk_title: pk_title,
    wire_frame: wire_frame,
    fill_in: fill_in,
    input_plantuml: input_plantuml,
    border_style: border_style,
    set_th_line: set_th_line,
    set_th_row: set_th_row,
    table_attr: table_attr,
    cancel_th_line: cancel_th_line,
    insert_line_right: insert_line_right,
    insert_row: insert_row,
    split_cell: split_cell,
    delete_line: delete_line,
    pk_no: pk_no,
    input_markdown_tip: input_markdown_tip,
    confirm_blank: confirm_blank,
    cancel_th_row: cancel_th_row,
    insert_row_above: insert_row_above,
    delete_table: delete_table,
    input_plantuml_tip: input_plantuml_tip,
    border_width: border_width,
    input_markdown: input_markdown,
    merge_cell: merge_cell,
    set_line_num: set_line_num,
    border_color: border_color,
    insert_line_left: insert_line_left,
    insert_row_below: insert_row_below,
    cancel_line_num: cancel_line_num,
    exit_data_lost_tip: exit_data_lost_tip,
    if_exit_gantt: if_exit_gantt,
    pk_gantt_loading: pk_gantt_loading,
    gantt_load_fail: gantt_load_fail,
    operate_error: operate_error,
    gantt_keep_work_tip: gantt_keep_work_tip,
    save_success: save_success,
    gantt_frame_load_fail: gantt_frame_load_fail,
    pk_network_exception: pk_network_exception,
    save_fail: save_fail,
    pk_saving: pk_saving,
    gantt_plugin_fail: gantt_plugin_fail,
    gantt_editing_tip: gantt_editing_tip,
    pk_discard: pk_discard,
    pk_zoom_in: pk_zoom_in,
    pk_zoom_out: pk_zoom_out,
    pk_use: pk_use,
    draft_edit_tip: draft_edit_tip,
    if_use_local_draft: if_use_local_draft,
    pk_load_fail: pk_load_fail,
    pk_this_time: pk_this_time,
    choose_time: choose_time,
    pk_hh_mm_ss: pk_hh_mm_ss,
    pk_y_d: pk_y_d,
    pk_y_d_ts: pk_y_d_ts,
    pk_text: pk_text,
    h1: h1,
    h2: h2,
    h3: h3,
    h4: h4,
    h5: h5,
    h6: h6,
    pk_catalog: pk_catalog,
    page_view_load_fail_click: page_view_load_fail_click,
    page_tree_no_sub_doc: page_tree_no_sub_doc,
    page_tree_no_perm: page_tree_no_perm,
    pk_load_fail_retry: pk_load_fail_retry,
    loading2: loading2,
    page_tree_deleted: page_tree_deleted,
    pk_pause: pk_pause,
    pk_mute: pk_mute,
    pk_untitled: pk_untitled,
    pk_play: pk_play,
    adjust_volume: adjust_volume,
    download_fail_retry: download_fail_retry,
    pk_download: pk_download,
    get_file_fail: get_file_fail,
    pk_network_fail: pk_network_fail,
    pk_upload_fail: pk_upload_fail,
    audio_not_support_tip: audio_not_support_tip,
    pk_volume: pk_volume,
    browser_not_support_tip: browser_not_support_tip,
    pk_zoom_in2: pk_zoom_in2,
    video_upload_tip: video_upload_tip,
    pk_video: pk_video,
    pk_zoom_out2: pk_zoom_out2,
    space_update_fail_tip: space_update_fail_tip,
    pk_recent_update: pk_recent_update,
    show_more: show_more,
    no_space_update: no_space_update,
    pk_restore: pk_restore
};

var locale$1 = "zh";
var version$2 = 16;
var cannot_search_user$1 = "未搜索到用户，点击空格完成输入";
var load_fail_retry$1 = "加载失败，请稍后重试";
var pk_avatar$1 = "头像";
var mention_by_mis_name$1 = "可通过姓名及mis号@用户";
var searching$1 = "搜索中...";
var pk_link_text$1 = "链接文字";
var insert_blank$1 = "插 入";
var pk_link$1 = "链接";
var pk_link_address$1 = "链接地址";
var pk_visit_link$1 = "访问链接";
var cancel_blank$1 = "取 消";
var pk_link_change_tip$1 = "链接文字跟随文档标题变更";
var pk_edit_link$1 = "编辑链接";
var pk_cancel_link$1 = "取消链接";
var pk_link_text_tip$1 = "请输入链接文字";
var save_blank$1 = "保 存";
var pk_link_address_tip$1 = "请输入链接地址";
var border$1 = "边框";
var pk_insert$1 = "插入";
var image_link$1 = "图片链接";
var pk_edit$1 = "编辑";
var pk_retry$1 = "重试";
var width$1 = "宽度";
var reupload$1 = "重新上传";
var image_upload_fail1$1 = "抱歉，图片上传失败！";
var image_upload_fail3$1 = "否则，请点击下方按钮进行重试。";
var right_address_tip$1 = "请输入正确的地址";
var image_upload_fail2$1 = "如果是从外部拖动、粘贴过来的图片，请下载原图并重新上传。";
var pk_confirm$1 = "确定";
var input_html$1 = "输入HTML代码";
var formual$1 = "公式";
var pk_loading$1 = "正在加载中...";
var pk_cancel$1 = "取消";
var click_to_expand$1 = "点击展开内容";
var input_html_tip$1 = "可以插入HTML代码，以在页面内嵌入相应内容：";
var insert_latex_tip$1 = "可以插入Latex代码，以在页面内嵌入相应内容：";
var input_latex$1 = "输入Latex代码";
var pk_title$1 = "标题";
var wire_frame$1 = "线框";
var fill_in$1 = "填充";
var input_plantuml$1 = "輸入PlantUML代碼";
var border_style$1 = "边框样式";
var set_th_row$1 = "设置表头行";
var set_th_line$1 = "设置表头列";
var table_attr$1 = "表格属性";
var cancel_th_line$1 = "取消表头列";
var insert_row$1 = "删除行";
var insert_line_right$1 = "右侧插入列";
var split_cell$1 = "拆分单元格";
var delete_line$1 = "删除列";
var confirm_blank$1 = "确 定";
var input_markdown_tip$1 = "可以插入Markdown代码，以在页面内嵌入相应内容：";
var pk_no$1 = "无";
var cancel_th_row$1 = "取消表头行";
var insert_row_above$1 = "上方插入行";
var delete_table$1 = "删除表格";
var input_plantuml_tip$1 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容";
var border_width$1 = "边框宽度";
var input_markdown$1 = "输入Markdown代码";
var merge_cell$1 = "合并单元格";
var set_line_num$1 = "设置行号列";
var border_color$1 = "边框颜色";
var insert_line_left$1 = "左侧插入列";
var insert_row_below$1 = "下方插入行";
var cancel_line_num$1 = "取消行号列";
var if_exit_gantt$1 = "确定退出甘特图?";
var exit_data_lost_tip$1 = "退出后，尚未保存的数据将丢失。";
var gantt_load_fail$1 = "甘特图载入失败";
var pk_gantt_loading$1 = "甘特图载入中...";
var operate_error$1 = "您的操作有误";
var gantt_keep_work_tip$1 = "请至少保留一个任务";
var gantt_frame_load_fail$1 = "甘特图框架加载失败";
var save_success$1 = "保存成功";
var save_fail$1 = "保存失败";
var pk_network_exception$1 = "网络异常";
var pk_saving$1 = "正在保存中...";
var gantt_plugin_fail$1 = "甘特图编辑插件启动失败";
var gantt_editing_tip$1 = "正在编辑甘特图，请稍候再试噢。";
var pk_discard$1 = "废弃";
var pk_zoom_in$1 = "放大查看";
var pk_zoom_out$1 = "缩小查看";
var pk_use$1 = "使用";
var draft_edit_tip$1 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继续编辑";
var pk_load_fail$1 = "加载失败";
var if_use_local_draft$1 = "是否使用本地草稿？";
var pk_this_time$1 = "此刻";
var choose_time$1 = "选择时间";
var pk_hh_mm_ss$1 = "HH:mm:ss";
var pk_y_d$1 = "YYYY-MM-DD";
var pk_y_d_ts$1 = "YYYY-MM-DD HH:mm:ss";
var pk_text$1 = "正文";
var h1$1 = "标题 1";
var h2$1 = "标题 2";
var h3$1 = "标题 3";
var h4$1 = "标题 4";
var h5$1 = "标题 5";
var h6$1 = "标题 6";
var pk_catalog$1 = "目录";
var page_view_load_fail_click$1 = "目录树加载失败，请点击重新加载";
var page_tree_no_sub_doc$1 = "该目录下暂无子文档";
var page_tree_no_perm$1 = "无权查看该目录树";
var pk_load_fail_retry$1 = "加载失败，请点击重新加载";
var loading2$1 = "正在加载";
var page_tree_deleted$1 = "该目录树已被删除";
var pk_pause$1 = "暂停";
var pk_mute$1 = "静音";
var pk_untitled$1 = "未命名";
var pk_play$1 = "播放";
var adjust_volume$1 = "音量调节";
var download_fail_retry$1 = "下载失败，请重试";
var get_file_fail$1 = "文件获取失败";
var pk_download$1 = "下载";
var pk_network_fail$1 = "网络连接失败";
var pk_upload_fail$1 = "上传失败";
var audio_not_support_tip$1 = "本浏览器不支持 HTML5 audio 标签";
var browser_not_support_tip$1 = "本浏览器不支持 HTML5 video 标签";
var pk_volume$1 = "音量";
var pk_zoom_in2$1 = "放大";
var pk_video$1 = "视频";
var video_upload_tip$1 = "视频正在转码中，如文件较大可能时间较长，请耐心等待…";
var pk_zoom_out2$1 = "缩小";
var space_update_fail_tip$1 = "空间更新获取失败，点击重试";
var pk_recent_update$1 = "最近更新";
var show_more$1 = "显示更多";
var no_space_update$1 = "空间暂时没有动态～";
var pk_restore$1 = "恢复默认";
var zh_parker = {
    locale: locale$1,
    version: version$2,
    cannot_search_user: cannot_search_user$1,
    load_fail_retry: load_fail_retry$1,
    pk_avatar: pk_avatar$1,
    mention_by_mis_name: mention_by_mis_name$1,
    searching: searching$1,
    pk_link_text: pk_link_text$1,
    insert_blank: insert_blank$1,
    pk_link: pk_link$1,
    pk_link_address: pk_link_address$1,
    pk_visit_link: pk_visit_link$1,
    cancel_blank: cancel_blank$1,
    pk_link_change_tip: pk_link_change_tip$1,
    pk_edit_link: pk_edit_link$1,
    pk_cancel_link: pk_cancel_link$1,
    pk_link_text_tip: pk_link_text_tip$1,
    save_blank: save_blank$1,
    pk_link_address_tip: pk_link_address_tip$1,
    border: border$1,
    pk_insert: pk_insert$1,
    image_link: image_link$1,
    pk_edit: pk_edit$1,
    pk_retry: pk_retry$1,
    width: width$1,
    reupload: reupload$1,
    image_upload_fail1: image_upload_fail1$1,
    image_upload_fail3: image_upload_fail3$1,
    right_address_tip: right_address_tip$1,
    image_upload_fail2: image_upload_fail2$1,
    pk_confirm: pk_confirm$1,
    input_html: input_html$1,
    formual: formual$1,
    pk_loading: pk_loading$1,
    pk_cancel: pk_cancel$1,
    click_to_expand: click_to_expand$1,
    input_html_tip: input_html_tip$1,
    insert_latex_tip: insert_latex_tip$1,
    input_latex: input_latex$1,
    pk_title: pk_title$1,
    wire_frame: wire_frame$1,
    fill_in: fill_in$1,
    input_plantuml: input_plantuml$1,
    border_style: border_style$1,
    set_th_row: set_th_row$1,
    set_th_line: set_th_line$1,
    table_attr: table_attr$1,
    cancel_th_line: cancel_th_line$1,
    insert_row: insert_row$1,
    insert_line_right: insert_line_right$1,
    split_cell: split_cell$1,
    delete_line: delete_line$1,
    confirm_blank: confirm_blank$1,
    input_markdown_tip: input_markdown_tip$1,
    pk_no: pk_no$1,
    cancel_th_row: cancel_th_row$1,
    insert_row_above: insert_row_above$1,
    delete_table: delete_table$1,
    input_plantuml_tip: input_plantuml_tip$1,
    border_width: border_width$1,
    input_markdown: input_markdown$1,
    merge_cell: merge_cell$1,
    set_line_num: set_line_num$1,
    border_color: border_color$1,
    insert_line_left: insert_line_left$1,
    insert_row_below: insert_row_below$1,
    cancel_line_num: cancel_line_num$1,
    if_exit_gantt: if_exit_gantt$1,
    exit_data_lost_tip: exit_data_lost_tip$1,
    gantt_load_fail: gantt_load_fail$1,
    pk_gantt_loading: pk_gantt_loading$1,
    operate_error: operate_error$1,
    gantt_keep_work_tip: gantt_keep_work_tip$1,
    gantt_frame_load_fail: gantt_frame_load_fail$1,
    save_success: save_success$1,
    save_fail: save_fail$1,
    pk_network_exception: pk_network_exception$1,
    pk_saving: pk_saving$1,
    gantt_plugin_fail: gantt_plugin_fail$1,
    gantt_editing_tip: gantt_editing_tip$1,
    pk_discard: pk_discard$1,
    pk_zoom_in: pk_zoom_in$1,
    pk_zoom_out: pk_zoom_out$1,
    pk_use: pk_use$1,
    draft_edit_tip: draft_edit_tip$1,
    pk_load_fail: pk_load_fail$1,
    if_use_local_draft: if_use_local_draft$1,
    pk_this_time: pk_this_time$1,
    choose_time: choose_time$1,
    pk_hh_mm_ss: pk_hh_mm_ss$1,
    pk_y_d: pk_y_d$1,
    pk_y_d_ts: pk_y_d_ts$1,
    pk_text: pk_text$1,
    h1: h1$1,
    h2: h2$1,
    h3: h3$1,
    h4: h4$1,
    h5: h5$1,
    h6: h6$1,
    pk_catalog: pk_catalog$1,
    page_view_load_fail_click: page_view_load_fail_click$1,
    page_tree_no_sub_doc: page_tree_no_sub_doc$1,
    page_tree_no_perm: page_tree_no_perm$1,
    pk_load_fail_retry: pk_load_fail_retry$1,
    loading2: loading2$1,
    page_tree_deleted: page_tree_deleted$1,
    pk_pause: pk_pause$1,
    pk_mute: pk_mute$1,
    pk_untitled: pk_untitled$1,
    pk_play: pk_play$1,
    adjust_volume: adjust_volume$1,
    download_fail_retry: download_fail_retry$1,
    get_file_fail: get_file_fail$1,
    pk_download: pk_download$1,
    pk_network_fail: pk_network_fail$1,
    pk_upload_fail: pk_upload_fail$1,
    audio_not_support_tip: audio_not_support_tip$1,
    browser_not_support_tip: browser_not_support_tip$1,
    pk_volume: pk_volume$1,
    pk_zoom_in2: pk_zoom_in2$1,
    pk_video: pk_video$1,
    video_upload_tip: video_upload_tip$1,
    pk_zoom_out2: pk_zoom_out2$1,
    space_update_fail_tip: space_update_fail_tip$1,
    pk_recent_update: pk_recent_update$1,
    show_more: show_more$1,
    no_space_update: no_space_update$1,
    pk_restore: pk_restore$1
};

var locale$2 = "zh-HK";
var version$3 = 16;
var cannot_search_user$2 = "未能找到用户，請按空格鍵完成輸入";
var load_fail_retry$2 = "載入失敗，請稍後重試。";
var pk_avatar$2 = "頭像";
var mention_by_mis_name$2 = "可通過姓名及MIS號碼@用戶";
var searching$2 = "搜尋中...";
var pk_link_text$2 = "鏈接文字";
var insert_blank$2 = "插 入";
var pk_visit_link$2 = "访问鏈接";
var pk_link_address$2 = "鏈接地址";
var pk_link$2 = "鏈接";
var cancel_blank$2 = "取 消";
var pk_link_change_tip$2 = "鏈接文字跟隨文件標題變更";
var pk_edit_link$2 = "編輯鏈接";
var pk_link_text_tip$2 = "請輸入鏈接文字";
var pk_cancel_link$2 = "取消鏈接";
var save_blank$2 = "保 存";
var pk_link_address_tip$2 = "請輸入鏈接地址";
var border$2 = "邊框";
var pk_insert$2 = "插入";
var image_link$2 = "圖片鏈接";
var pk_retry$2 = "重試";
var pk_edit$2 = "編輯";
var width$2 = "寬度";
var reupload$2 = "重新上傳";
var image_upload_fail1$2 = "抱歉，圖片上傳失敗！";
var right_address_tip$2 = "請輸入正確的地址";
var image_upload_fail3$2 = "否則，請按下方按鈕進行重試。";
var image_upload_fail2$2 = "如果您從外部拖動、粘貼過來的圖片，請下載原圖並重新上傳。";
var pk_confirm$2 = "確定";
var input_html$2 = "輸入HTML代碼";
var formual$2 = "公式";
var pk_loading$2 = "正在載入中...";
var pk_cancel$2 = "取消";
var click_to_expand$2 = "點選展開內容";
var input_html_tip$2 = "可以插入HTML代碼，以在頁面內嵌入相應內容：";
var insert_latex_tip$2 = "可以插入Latex代碼，以在頁面內嵌入相應內容：";
var input_latex$2 = "输入LaTeX代码";
var pk_title$2 = "標題";
var wire_frame$2 = "線框";
var fill_in$2 = "填充";
var input_plantuml$2 = "輸入PlantUML程式碼";
var border_style$2 = "邊框樣式";
var set_th_row$2 = "設置表頭行";
var set_th_line$2 = "設置表頭列";
var table_attr$2 = "表格屬性";
var cancel_th_line$2 = "取消表頭列";
var insert_row$2 = "删除行";
var insert_line_right$2 = "右側插入列";
var split_cell$2 = "拆分儲存格";
var delete_line$2 = "删除列";
var pk_no$2 = "無";
var confirm_blank$2 = "確 定";
var input_markdown_tip$2 = "可以插入Markdown代碼，以在頁面內嵌入相應內容：";
var cancel_th_row$2 = "取消表頭行";
var insert_row_above$2 = "上方插入行";
var delete_table$2 = "刪除表格";
var input_plantuml_tip$2 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容：";
var border_width$2 = "邊框寬度";
var input_markdown$2 = "輸入Markdown代碼";
var merge_cell$2 = "合併儲存格";
var set_line_num$2 = "設置行号列";
var border_color$2 = "邊框顏色";
var insert_line_left$2 = "左側插入列";
var insert_row_below$2 = "下方插入行";
var cancel_line_num$2 = "取消行号列";
var if_exit_gantt$2 = "確定退出甘特圖?";
var exit_data_lost_tip$2 = "退出後，尚未儲存的數據將會遺失。";
var pk_gantt_loading$2 = "甘特圖載入中...";
var gantt_load_fail$2 = "甘特圖載入失敗";
var operate_error$2 = "您的操作有誤";
var gantt_keep_work_tip$2 = "請至少保留一個任務";
var save_success$2 = "保存成功";
var gantt_frame_load_fail$2 = "甘特圖框架載入失敗";
var save_fail$2 = "保存失敗";
var pk_network_exception$2 = "網絡異常";
var pk_saving$2 = "正在儲存中...";
var gantt_plugin_fail$2 = "甘特圖編輯插件啟動失敗。";
var gantt_editing_tip$2 = "正在編輯甘特圖，請稍後再試唷。";
var pk_discard$2 = "廢棄";
var pk_zoom_in$2 = "放大查看";
var pk_zoom_out$2 = "縮小查看";
var pk_use$2 = "使用";
var draft_edit_tip$2 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继續編輯";
var pk_load_fail$2 = "加載失敗";
var if_use_local_draft$2 = "是否使用本地草稿？";
var pk_this_time$2 = "此刻";
var choose_time$2 = "選擇時間";
var pk_hh_mm_ss$2 = "HH:mm:ss";
var pk_y_d$2 = "YYYY-MM-DD";
var pk_y_d_ts$2 = "YYYY-MM-DD HH:mm:ss";
var pk_text$2 = "正文";
var h1$2 = "標題 1";
var h2$2 = "標題 2";
var h3$2 = "標題 3";
var h4$2 = "標題 4";
var h5$2 = "標題 5";
var h6$2 = "標題 6";
var pk_catalog$2 = "目錄";
var page_view_load_fail_click$2 = "目錄樹加載失敗，請點擊重新加載。";
var page_tree_no_sub_doc$2 = "該目錄下暫無子文檔。";
var page_tree_no_perm$2 = "無權查看該目錄樹";
var pk_load_fail_retry$2 = "加載失敗，請點擊重新加載。";
var loading2$2 = "正在載入";
var page_tree_deleted$2 = "該目錄樹已被刪除";
var pk_pause$2 = "暫停";
var pk_mute$2 = "靜音";
var pk_untitled$2 = "未命名";
var pk_play$2 = "播放";
var adjust_volume$2 = "音量調節";
var download_fail_retry$2 = "下載失敗，請重試";
var pk_download$2 = "下載";
var get_file_fail$2 = "文件獲取失敗";
var pk_network_fail$2 = "網絡連接失敗";
var pk_upload_fail$2 = "上載失敗";
var audio_not_support_tip$2 = "本瀏覽器不支援 HTML5 audio 標籤";
var pk_volume$2 = "音量";
var browser_not_support_tip$2 = "本瀏覽器不支援 HTML5 video 標籤";
var pk_zoom_in2$2 = "放大";
var video_upload_tip$2 = "視頻正在轉碼中，如果文件較大可能需要較長的時間，請耐心等待...";
var pk_video$2 = "視頻";
var pk_zoom_out2$2 = "縮小";
var space_update_fail_tip$2 = "空間更新獲取失敗，點擊重試";
var pk_recent_update$2 = "最近更新";
var show_more$2 = "顯示更多";
var no_space_update$2 = "空間暫時沒有動態～";
var pk_restore$2 = "恢復預設";
var zhHK_parker = {
    locale: locale$2,
    version: version$3,
    cannot_search_user: cannot_search_user$2,
    load_fail_retry: load_fail_retry$2,
    pk_avatar: pk_avatar$2,
    mention_by_mis_name: mention_by_mis_name$2,
    searching: searching$2,
    pk_link_text: pk_link_text$2,
    insert_blank: insert_blank$2,
    pk_visit_link: pk_visit_link$2,
    pk_link_address: pk_link_address$2,
    pk_link: pk_link$2,
    cancel_blank: cancel_blank$2,
    pk_link_change_tip: pk_link_change_tip$2,
    pk_edit_link: pk_edit_link$2,
    pk_link_text_tip: pk_link_text_tip$2,
    pk_cancel_link: pk_cancel_link$2,
    save_blank: save_blank$2,
    pk_link_address_tip: pk_link_address_tip$2,
    border: border$2,
    pk_insert: pk_insert$2,
    image_link: image_link$2,
    pk_retry: pk_retry$2,
    pk_edit: pk_edit$2,
    width: width$2,
    reupload: reupload$2,
    image_upload_fail1: image_upload_fail1$2,
    right_address_tip: right_address_tip$2,
    image_upload_fail3: image_upload_fail3$2,
    image_upload_fail2: image_upload_fail2$2,
    pk_confirm: pk_confirm$2,
    input_html: input_html$2,
    formual: formual$2,
    pk_loading: pk_loading$2,
    pk_cancel: pk_cancel$2,
    click_to_expand: click_to_expand$2,
    input_html_tip: input_html_tip$2,
    insert_latex_tip: insert_latex_tip$2,
    input_latex: input_latex$2,
    pk_title: pk_title$2,
    wire_frame: wire_frame$2,
    fill_in: fill_in$2,
    input_plantuml: input_plantuml$2,
    border_style: border_style$2,
    set_th_row: set_th_row$2,
    set_th_line: set_th_line$2,
    table_attr: table_attr$2,
    cancel_th_line: cancel_th_line$2,
    insert_row: insert_row$2,
    insert_line_right: insert_line_right$2,
    split_cell: split_cell$2,
    delete_line: delete_line$2,
    pk_no: pk_no$2,
    confirm_blank: confirm_blank$2,
    input_markdown_tip: input_markdown_tip$2,
    cancel_th_row: cancel_th_row$2,
    insert_row_above: insert_row_above$2,
    delete_table: delete_table$2,
    input_plantuml_tip: input_plantuml_tip$2,
    border_width: border_width$2,
    input_markdown: input_markdown$2,
    merge_cell: merge_cell$2,
    set_line_num: set_line_num$2,
    border_color: border_color$2,
    insert_line_left: insert_line_left$2,
    insert_row_below: insert_row_below$2,
    cancel_line_num: cancel_line_num$2,
    if_exit_gantt: if_exit_gantt$2,
    exit_data_lost_tip: exit_data_lost_tip$2,
    pk_gantt_loading: pk_gantt_loading$2,
    gantt_load_fail: gantt_load_fail$2,
    operate_error: operate_error$2,
    gantt_keep_work_tip: gantt_keep_work_tip$2,
    save_success: save_success$2,
    gantt_frame_load_fail: gantt_frame_load_fail$2,
    save_fail: save_fail$2,
    pk_network_exception: pk_network_exception$2,
    pk_saving: pk_saving$2,
    gantt_plugin_fail: gantt_plugin_fail$2,
    gantt_editing_tip: gantt_editing_tip$2,
    pk_discard: pk_discard$2,
    pk_zoom_in: pk_zoom_in$2,
    pk_zoom_out: pk_zoom_out$2,
    pk_use: pk_use$2,
    draft_edit_tip: draft_edit_tip$2,
    pk_load_fail: pk_load_fail$2,
    if_use_local_draft: if_use_local_draft$2,
    pk_this_time: pk_this_time$2,
    choose_time: choose_time$2,
    pk_hh_mm_ss: pk_hh_mm_ss$2,
    pk_y_d: pk_y_d$2,
    pk_y_d_ts: pk_y_d_ts$2,
    pk_text: pk_text$2,
    h1: h1$2,
    h2: h2$2,
    h3: h3$2,
    h4: h4$2,
    h5: h5$2,
    h6: h6$2,
    pk_catalog: pk_catalog$2,
    page_view_load_fail_click: page_view_load_fail_click$2,
    page_tree_no_sub_doc: page_tree_no_sub_doc$2,
    page_tree_no_perm: page_tree_no_perm$2,
    pk_load_fail_retry: pk_load_fail_retry$2,
    loading2: loading2$2,
    page_tree_deleted: page_tree_deleted$2,
    pk_pause: pk_pause$2,
    pk_mute: pk_mute$2,
    pk_untitled: pk_untitled$2,
    pk_play: pk_play$2,
    adjust_volume: adjust_volume$2,
    download_fail_retry: download_fail_retry$2,
    pk_download: pk_download$2,
    get_file_fail: get_file_fail$2,
    pk_network_fail: pk_network_fail$2,
    pk_upload_fail: pk_upload_fail$2,
    audio_not_support_tip: audio_not_support_tip$2,
    pk_volume: pk_volume$2,
    browser_not_support_tip: browser_not_support_tip$2,
    pk_zoom_in2: pk_zoom_in2$2,
    video_upload_tip: video_upload_tip$2,
    pk_video: pk_video$2,
    pk_zoom_out2: pk_zoom_out2$2,
    space_update_fail_tip: space_update_fail_tip$2,
    pk_recent_update: pk_recent_update$2,
    show_more: show_more$2,
    no_space_update: no_space_update$2,
    pk_restore: pk_restore$2
};

// This file was auto generated by resource-sync tools.
var resources = {
    "en": {
        parker: en_parker
    },
    "zh": {
        parker: zh_parker
    },
    "zh-HK": {
        parker: zhHK_parker
    }
};

function _classCallCheck$5(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var I18nManager = /*#__PURE__*/ function() {
    function I18nManager() {
        _classCallCheck$5(this, I18nManager);
    }
    I18nManager.getInstance = function getInstance() {
        if (this.instance === null) {
            this.instance = createInstance();
            this.instance.init({
                // lng: 'zh',
                fallbackLng: "zh",
                ns: [
                    "parker"
                ],
                defaultNS: "parker",
                resources: resources,
                interpolation: {
                    escapeValue: false
                },
                react: {
                    useSuspense: false
                }
            });
            window.parkerI18nClient = this.instance;
        }
        return this.instance;
    };
    return I18nManager;
}();
I18nManager.instance = null;
var i18nClient = I18nManager.getInstance();

function _classCallCheck$6(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties$2(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass$2(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties$2(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties$2(Constructor, staticProps);
    return Constructor;
}
function _defineProperty$5(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$5(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$5(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$4(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps$4(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$4(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var NodeExtension = /*#__PURE__*/ function() {
    function NodeExtension() {
        var options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : Object.create(null);
        _classCallCheck$6(this, NodeExtension);
        this.options = _objectSpread$5({}, this.defaultOptions, options);
    }
    var _proto = NodeExtension.prototype;
    _proto.createNode = function createNode(type, attrs, content, marks) {
        return function(state, dispatch, view) {
            var tr = state.tr, from = state.selection.from;
            dispatch(tr.insert(from, type.create(attrs, content, marks)));
            return true;
        };
    };
    _createClass$2(NodeExtension, [
        {
            key: "type",
            get: function get() {
                return ExtensionType.Node;
            }
        },
        {
            key: "defaultOptions",
            get: function get() {
                return {};
            }
        }
    ]);
    return NodeExtension;
}();
var MarkExtension = /*#__PURE__*/ function() {
    function MarkExtension() {
        _classCallCheck$6(this, MarkExtension);
    }
    _createClass$2(MarkExtension, [
        {
            key: "type",
            get: function get() {
                return ExtensionType.Mark;
            }
        }
    ]);
    return MarkExtension;
}();
var CommonExtension = /*#__PURE__*/ function() {
    function CommonExtension() {
        var options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : Object.create(null);
        _classCallCheck$6(this, CommonExtension);
        this.options = _objectSpread$5({}, this.defaultOptions, options);
    }
    _createClass$2(CommonExtension, [
        {
            key: "defaultOptions",
            get: function get() {
                return {};
            }
        }
    ]);
    return CommonExtension;
}();
var EmbedExtension = /*#__PURE__*/ function() {
    function EmbedExtension() {
        var _ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, getState = _ref.getState, providers = _ref.providers, getView = _ref.getView, editorEventEmitter = _ref.editorEventEmitter;
        var _this = this;
        _classCallCheck$6(this, EmbedExtension);
        this.editing = "" // 不在编辑为空，新建'create'，编辑'edit'
        ;
        this.xml = "";
        this.create = function(onLoaded) {
            if (_this.editing) {
                return;
            }
            _this.editing = "create";
            _this.xml = "";
            _this.open(onLoaded);
        };
        this.open = function(onLoaded) {
            if (_this.iframe) return;
            _this.bindEvent();
            _this.iframe = document.createElement("iframe");
            _this.iframe.setAttribute("frameborder", "0");
            _this.iframe.setAttribute("class", "ct-".concat(_this.name, "-frame"));
            _this.iframe.setAttribute("data-type", "ct-frame");
            _this.iframe.style.opacity = 0;
            _this.showLoading();
            _this.iframe.onload = function() {
                onLoaded === null || onLoaded === void 0 ? void 0 : onLoaded();
                _this.iframe.style.opacity = 1;
                _this.hideLoading();
            };
            _this.iframe.setAttribute("src", _this.serverUrl);
            document.body.appendChild(_this.iframe);
        };
        this.close = function() {
            document.body.removeChild(_this.iframe);
            _this.hideLoading();
            _this.removeBindEvent();
            _this.iframe = null;
            _this.editing = "";
        };
        this.postMessage = function(params) {
            if (!_this.iframe) return;
            _this.iframe.contentWindow.postMessage(JSON.stringify(params), "*");
        };
        this.bindEvent = function() {
            window.addEventListener("message", _this.receive);
            window.addEventListener("popstate", _this.close);
        };
        this.removeBindEvent = function() {
            window.removeEventListener("popstate", _this.close);
            window.removeEventListener("message", _this.receive);
        };
        this.cleanEdit = function() {
            if (_this.editing === "create") return;
            var _this_getView = _this.getView(), state = _this_getView.state, dispatch = _this_getView.dispatch;
            var schema = state.schema, tr = state.tr, doc = state.doc, selection = state.selection;
            var from = selection.from;
            var node = doc.nodeAt(from);
            dispatch(tr.setNodeMarkup(from, schema.nodes.drawio, _objectSpreadProps$4(_objectSpread$5({}, node.attrs), {
                _editing: 0
            })));
        };
        this.showLoading = function() {
            var loadingMsg = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : i18nClient.t("pk_loading", "正在加载中..."), showCancel = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
            var $loading = document.createElement("div");
            var cancelText = i18nClient.t("pk_cancel", "取消");
            $loading.className = "ct-".concat(_this.name, "-loading");
            $loading.innerHTML = '\n      <div class="loading-container">\n        <div class="loading-icon"><i></i><i></i><i></i></div>\n        <div class="loading-tips">'.concat(loadingMsg, "</div>\n        ").concat(showCancel ? '<div class="loading-cancel"> <a class="btn-cancel">'.concat(cancelText, "</a></div>") : "", "\n      </div>");
            if (showCancel) {
                $loading.querySelector(".btn-cancel").addEventListener("click", _this.close, false);
            }
            document.body.appendChild($loading);
        };
        this.hideLoading = function() {
            var node = document.querySelector(".ct-".concat(_this.name, "-loading"));
            node && document.body.removeChild(node);
        };
        // TODO: 框架需要注入此处依赖的对象
        this.providers = providers;
        this.getState = getState;
        this.getView = getView;
        this.editorEventEmitter = editorEventEmitter;
    }
    _createClass$2(EmbedExtension, [
        {
            key: "type",
            get: function get() {
                return ExtensionType.NodeEmbed;
            }
        },
        {
            key: "name",
            get: function get() {
                return "iframe";
            }
        }
    ]);
    return EmbedExtension;
}();

export { CommonExtension, Editor, EmbedExtension, Manager, MarkExtension, NodeExtension, Providers };
//# sourceMappingURL=index.js.map
